import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { SearchOutlined } from '@ant-design/icons';
import { Input } from 'components/FormFields';
import Table from 'components/Table';
import Pagination from 'components/Pagination';
import ROUTES from 'routes';
import { PAGINATION } from 'helper/const';
import { onEditRow } from 'utils/table';
import toast from 'helper/toast';
import { getErrorMessageFromObj } from 'utils/object';
import { applySearch } from 'utils';
import { automatedOrderColumns } from './utils';
import { useIndexData, useActions } from './selectorData';
import classes from './ScheduledTab.module.scss';

const ScheduledTab = ({
  companyId,
  permission,
  canManageScheduledServices,
  isActive,
  onEditDraftOrder,
  scheduledOrdersRefreshTrigger,
}) => {
  const notAdmin = !permission.isAdmin;
  const history = useHistory();
  const [keyword, setKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const {
    automatedOrders,
    automatedOrdersLoading,
    automatedOrdersTotal,
    deleteOrderLoading,
  } = useIndexData();
  const { getAutomatedOrders, deleteOrder } = useActions();

  const reloadData = (val, page) => {
    setCurrentPage(page);
    getAutomatedOrders(companyId, { page, search: val });
  };

  useEffect(() => {
    if (isActive && companyId) {
      reloadData(keyword, 1);
    }
    // eslint-disable-next-line
  }, [isActive, companyId]);

  useEffect(() => {
    if (scheduledOrdersRefreshTrigger && companyId) {
      reloadData(keyword, currentPage);
    }
    // eslint-disable-next-line
  }, [scheduledOrdersRefreshTrigger]);

  const handleSearch = val => {
    reloadData(val, 1);
  };

  const goToOrderDetailPage = id => {
    if (notAdmin) {
      history.push(ROUTES.COMPANY_ORDER({ id: companyId, orderId: id }));
    } else {
      history.push(ROUTES.CLIENT_ORDER_DETAIL({ id: companyId, orderId: id }));
    }
  };

  const onDelete = record => {
    deleteOrder(companyId, record.id, (res, err) => {
      if (res) {
        reloadData(keyword, 1);
        toast.success('Order deleted successfully.');
      } else {
        toast.error(getErrorMessageFromObj(err)[0]);
      }
    });
  };

  const onEditDraft = record => {
    if (onEditDraftOrder) {
      onEditDraftOrder(record);
    }
  };

  const columns = automatedOrderColumns({
    onView: record => goToOrderDetailPage(record.id),
    onDelete,
    onEditDraft,
    canManageScheduledServices,
  });

  return (
    <div>
      <div className={classes.tabContentHeader}>
        <Input
          size="medium"
          name="keyword"
          value={keyword}
          placeholder="Search orders"
          allowClear
          onChange={e => {
            const value = e.target.value;
            setKeyword(value);
            if (!value) {
              handleSearch('');
            } else {
              applySearch(value, val => {
                handleSearch(val);
              });
            }
          }}
          prefix={<SearchOutlined />}
          isBlue
        />
      </div>
      <Table
        columns={columns}
        data={automatedOrders}
        rowKey="id"
        loading={automatedOrdersLoading || deleteOrderLoading}
        isScroll
        scroll={{ x: 850 }}
        rowIsClick
        onRow={record => ({
          className: record.highlight ? classes.highlightRow : '',
          onClick: event => {
            onEditRow(event, () => {
              goToOrderDetailPage(record.id);
            });
          },
        })}
      />
      {automatedOrdersTotal > 0 && (
        <Pagination
          current={currentPage}
          pageSize={PAGINATION.PER_PAGE}
          total={automatedOrdersTotal}
          right
          onChange={page => {
            reloadData(keyword, page);
          }}
          className={classes.pagination}
        />
      )}
    </div>
  );
};

export default ScheduledTab;
